import { Input, MMButton, ModalContentSurface, ModalFooter, ModalSubtitle, ModalTitle } from '@components'
import cn from '@integrations/classnames'
import { useContext, useMemo } from '@integrations/react'
import { useAccountCreateQry } from 'mmfintech-backend-api'
import { GlobalContext, tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import styles from './AccountCreateModal.module.scss'
import Label from './Label'

interface Props {
  filteredCurrencies?: string[]
  onSuccess?: (currencyCode: string, accountId: number) => void
}

function AccountCreateModal({ filteredCurrencies = [], onSuccess }: Props) {
  const { modalHide } = useContext(GlobalContext)
  const { currencies, formValues, createAccountError, createAccountFetching, handleSubmit } = useAccountCreateQry({
    filteredCurrencies,
    onSuccess
  })

  const options = useMemo(
    () =>
      currencies?.map(({ value, label }) => {
        return {
          value: value,
          label: <Label currency={value} label={label} />
        }
      }) ?? [],
    [currencies]
  )

  return (
    <ModalContentSurface className={cn('account-create-wrapper')} data-test='create-account-modal'>
      <div className={cn(styles['account-create-header'], 'account-create-header')}>
        <ModalTitle className={cn(styles['account-create-title'], 'account-create-title')}>
          {tr('FRONTEND.ACCOUNTS.CREATE.TITLE', 'New account')}
        </ModalTitle>
        <ModalSubtitle>{tr('FRONTEND.ACCOUNTS.CREATE.SUBTITLE', 'Open another currency account.')}</ModalSubtitle>
      </div>

      <form className={cn(styles['account-create-form'], 'account-create-form')} noValidate>
        <Input
          type='select'
          maxMenuHeight={250}
          data-test='currency'
          label={tr('FRONTEND.ACCOUNTS.CREATE.CURRENCY_LABEL', 'Currency')}
          options={options}
          onChange={(name: string, value: string) => formValues.setValue(name, value)}
          {...formValues.registerShort('currency')}
        />

        <Input
          type='text'
          data-test='account-name'
          label={tr('FRONTEND.ACCOUNTS.CREATE.NAME_LABEL', 'Account name')}
          {...formValues.registerInput('name')}
        />

        <ErrorDisplay error={createAccountError} />

        <ModalFooter>
          <MMButton
            type='button'
            variant='secondary'
            caption={tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
            onClick={modalHide}
            data-test='button-cancel'
          />
          <MMButton
            type='button'
            variant='primary'
            caption={tr('FRONTEND.ACCOUNTS.CREATE.SUBMIT_BUTTON', 'Add')}
            onClick={handleSubmit}
            loading={createAccountFetching}
            data-test='button-submit'
          />
        </ModalFooter>
      </form>
    </ModalContentSurface>
  )
}

export default AccountCreateModal
